import {
    AmbulanceCertificationConditionCode,
    AmbulanceTransportReasonCodeType,
    Amount,
    AuthorizationRequiredType,
    CheckoutChargeStatusType,
    ClaimEligibilityItem,
    ClaimGuarantorResponsibility,
    ClaimResponsibilityType,
    ClaimTransmissionStatus,
    ClaimTransmissionStatusType,
    ClaimType,
    CoverageLevelType,
    CreditCardBrandType,
    CurrencyType,
    DMECertificationTypeCode,
    DMEConditionIndicatorCodeType,
    DMERentalUnitPriceIndicatorCodeType,
    DMEReportTransmissionCodeType,
    DrugMeasurementCode,
    EligibilityItemType,
    FormType,
    InPlanNetworkType,
    Invoice,
    InvoiceItem,
    InvoiceStatusType,
    InvoiceType,
    ListProviderClaimsRequest,
    ListProviderGuarantorsRequest,
    ListProviderLettersRequest,
    ListProviderMailCartsRequest,
    ListProviderMailRequest,
    ListProviderPatientsRequest,
    ListProviderPayersRequest,
    ListProviderProvidersRequest,
    ListProviderReceiveFaxesRequest,
    ListProviderSendFaxesRequest,
    MailCartShipmentMethod,
    MailCartShipmentTracking,
    MedicareType,
    ObjectNoResponse,
    PaperworkReportCode,
    PaperworkTransmissionCode,
    PayerAdjustmentGroupType,
    PaymentPlanIntervalType,
    PaymentStatusType,
    PlaceOfServiceType,
    PlanType,
    ProcedureCodeType,
    ProfessionalTemplateAmbulanceInformation,
    ProfessionalTemplateAnesthesiaInformation,
    // @ts-expect-error Will be solved later after support api is updated
    ProfessionalTemplateDrugInformation,
    ProfessionalTemplateDurableMedicalEquipmentInformation,
    ProfessionalTemplateForm,
    // @ts-expect-error Will be solved later after support api is updated
    ProfessionalTemplatePaperwork,
    ProfessionalTemplateTestResult,
    ProviderChargemasterItem,
    ProviderClaim,
    ProviderClaimEligibility,
    ProviderClaimPayment,
    ProviderClaimPaymentItem,
    ProviderClaimPaymentItemAdjustment,
    ProviderClaimTransmission,
    ProviderCollectionPayment, ProviderCollectionStatusType,
    ProviderCreateCollectionPaymentPayload,
    ProviderDisplayDocumentType,
    ProviderDocument,
    ProviderDocumentUrl,
    ProviderFacility,
    ProviderGuarantor,
    ProviderLetter,
    ProviderMail,
    ProviderMailCart,
    ProviderMailCartShipment,
    ProviderMedicalClaim,
    ProviderPatient,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderPaymentPlan,
    ProviderProfessionalClaim,
    ProviderProfessionalClaimTemplate,
    ProviderProvider,
    ProviderReceiveFax,
    ProviderSeller,
    ProviderSendFax,
    ProviderStatus,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    ProviderType,
    QuantityType,
    RelationshipType,
    ServiceType,
    SexType,
    ShortUrlType,
    StateType,
    StatusItem,
    SupportCheckoutCharge,
    SupportCheckoutChargeRefund,
    SupportCreditCard,
    SupportHL7Message,
    SupportLetterAddress,
    SupportLetterResponse,
    SupportLetterTrackingEvent,
    SupportProfessionalTemplateServiceLine,
    SupportRefundCollectionPaymentPayload,
    SupportShortUrl,
    TemporaryDocumentUrl,
    TestResultIdentificationCode,
    TestResultMeasurementQualifier,
    TimePeriodType,
    Transaction,
    TransactionStatusType,
} from '@enter/core';
import * as d3 from 'd3';
import { format } from 'date-fns';
import { faker } from '@faker-js/faker';
import _ from 'lodash';
import {IClaimDetail} from './claim.store';
import {ICommunication, IItemListing} from './root.store';
import CMSExample from '@test-documents/CMS-example.pdf';
import FaxExample from '@test-documents/FAX-example.pdf';
import {isDate, isUuid, payerNames, waitForNoReason} from './util';
import {
    generateAddress,
    generateAmount,
    generateItemListing,
    hasIdIn,
    like,
    oneOf,
    parseAmount,
    sortItems,
    subsetOf,
    upToNTimes,
} from './util.generator';

export interface ClaimGeneratorClaimOptions {
    payers?: ProviderPayer[];
    fromDos?: Date,
    throughDos?: Date,
    acceptedPercentageOfCharge?: number
}


export class ClaimGenerator {
    public startDate: Date;
    public endDate: Date;

    public chargemasterItems: ProviderChargemasterItem[] = [];
    public checkoutCharges: SupportCheckoutCharge[] = [];
    public claims: ProviderClaim[] = [];
    public collections: ProviderCollectionPayment[] = [];
    public creditCards: SupportCreditCard[] = [];
    public documents: ProviderDocument[] = [];
    public documentUrls: ProviderDocumentUrl[] = [];
    public eligibilities: ProviderClaimEligibility[] = [];
    public facilities: ProviderFacility[] = [];
    public guarantors: ProviderGuarantor[] = [];
    public hl7Messages: SupportHL7Message[] = [];
    public invoices: Invoice[] = [];
    public letters: ProviderLetter[] = [];
    public mails: ProviderMail[] = [];
    public communications: ICommunication[] = [];
    public mailCarts: ProviderMailCart[] = [];
    public patients: ProviderPatient[] = [];
    public payerSubscribers: ProviderPayerSubscriber[] = [];
    public payers: ProviderPayer[] = [];
    public paymentPlans: ProviderPaymentPlan[] = [];
    public payments: ProviderClaimPayment[] = [];
    public professionalClaims: ProviderProfessionalClaim[] = [];
    public professionalTemplates: ProviderProfessionalClaimTemplate[] = [];
    public providers: ProviderProvider[] = [];
    public receiveFaxes: ProviderReceiveFax[] = [];
    public sellers: ProviderSeller[] = [];
    public sendFaxes: ProviderSendFax[] = [];
    public shortUrls: SupportShortUrl[] = [];
    public statuses: ProviderStatus[] = [];
    public subscribers: ProviderSubscriber[] = [];
    public subscriberPatients: ProviderSubscriberPatient[] = [];
    public temporaryDocumentUrls: TemporaryDocumentUrl[] = [];
    public transactions: Transaction[] = [];
    public transmissions: ProviderClaimTransmission[] = [];

    // TODO: extract index/storeTo/loadFrom as utility class
    public index: Map<string, Map<string, string[]>> = new Map<string, Map<string, string[]>>();

    constructor() {
        this.endDate = new Date();
        this.startDate = d3.utcDay.offset(this.endDate, -365);
        this.generateEntities(10);
    }

    public generateClaimListing = async (req: ListProviderClaimsRequest): Promise<IItemListing<ProviderClaim>> => {
        const claims = this.claims;
        await waitForNoReason(400);
        const sorted = sortItems(claims, req.sort, req.order);
        return generateItemListing(req, sorted);
    };

    public generateClaim = async (claimId: string, claimGeneratorOptions?: ClaimGeneratorClaimOptions | undefined): Promise<ProviderClaim | undefined> => {
        let claim: ProviderClaim | undefined = _.find(this.claims, { id: claimId });
        if (!claim) {
            claim = this.generateProviderClaim(claimId);
        }
        await waitForNoReason(400);
        return claim;
    };

    public generateClaimDetail = async (claim?: ProviderClaim): Promise<IClaimDetail> => {
        if (!claim || !claim.id) {
            throw new Error(`No claim provided for generateClaimDetail`);
        }
        const claimId = claim.id as string;

        // depend on claim and id
        const chargemasterItems = this.loadFromIndex<ProviderChargemasterItem>('chargemasterItems', claimId);
        const collections = this.loadFromIndex<ProviderCollectionPayment>('collections', claimId);
        const documents = this.loadFromIndex<ProviderDocument>('documents', claimId);
        const eligibilities = this.loadFromIndex<ProviderClaimEligibility>('eligibilities', claimId);
        const hl7Messages = this.loadFromIndex<SupportHL7Message>('hl7Messages', claimId);
        const invoices = this.loadFromIndex<Invoice>('invoices', claimId);
        const letters = this.loadFromIndex<ProviderLetter>('letters', claimId);
        const mails = this.loadFromIndex<ProviderMail>('mails', claimId);
        const payments = this.loadFromIndex<ProviderClaimPayment>('payments', claimId);
        const receiveFaxes = this.loadFromIndex<ProviderReceiveFax>('receiveFaxes', claimId);
        const sendFaxes = this.loadFromIndex<ProviderSendFax>('sendFaxes', claimId);
        const statuses = this.loadFromIndex<ProviderStatus>('statuses', claimId);
        const transactions = this.loadFromIndex<Transaction>('transactions', claimId);
        const transmissions = this.loadFromIndex<ProviderClaimTransmission>('transmissions', claimId);

        // dereferenced
        const professionalClaims = this.loadFromIndex<ProviderProfessionalClaim>('professionalClaims', claimId);
        const professionalTemplates = this.loadFromIndex<ProviderProfessionalClaimTemplate>(
            'professionalTemplates',
            claimId,
        );
        const professionalClaim = _.first<ProviderProfessionalClaim>(professionalClaims);
        const professionalTemplate = _.first<ProviderProfessionalClaimTemplate>(professionalTemplates);

        const {
            facility,
            guarantor,
            patients,
            payerSubscribers,
            payers,
            providers,
            seller,
            subscriberPatients,
            subscribers,
        } = this.resolveProfessionalClaim(professionalClaim);

        const guarantorId = _.get(guarantor, 'id', '');
        const checkoutCharges = this.loadFromIndex<SupportCheckoutCharge>('checkoutCharges', guarantorId);
        const creditCards = this.loadFromIndex<SupportCreditCard>('creditCards', guarantorId);

        const loadPaymentPlans = (key: string): ProviderPaymentPlan[] =>
            this.loadFromIndex<ProviderPaymentPlan>('paymentPlans', key) || [];
        const invoicePaymentPlans = _.flatMap(invoices, invoice => loadPaymentPlans(_.get(invoice, 'id', '')));
        const guarantorPaymentPlans = loadPaymentPlans(guarantorId);
        const paymentPlans = _.compact(_.concat<ProviderPaymentPlan>([], invoicePaymentPlans, guarantorPaymentPlans));

        return {
            chargemasterItems,
            checkoutCharges,
            claim,
            collections,
            creditCards,
            documents,
            eligibilities,
            facility,
            guarantor,
            hl7Messages,
            invoices,
            letters,
            mails,
            patients,
            payers,
            payerSubscribers,
            paymentPlans,
            payments,
            professionalClaim,
            professionalTemplate,
            providers,
            receiveFaxes,
            seller,
            sendFaxes,
            statuses,
            subscribers,
            subscriberPatients,
            transactions,
            transmissions,
        };
    };

    public generateBasicSearchResults = async (term: string): Promise<ProviderClaim[]> => {
        await waitForNoReason(400);
        const searchTerm = _.trim(term);
        return _.compact(_.filter(this.claims, this.matchesClaim(searchTerm)));
    };

    public queryPatients = async (query: string): Promise<ProviderPatient[]> => {
        return _.filter(
            this.patients,
            patient => _.startsWith(patient.first, query) || _.startsWith(patient.last, query),
        );
    };
    public querySellers = async (query: string): Promise<ProviderSeller[]> => {
        return _.filter(this.sellers, seller => _.startsWith(seller.first, query) || _.startsWith(seller.last, query));
    };
    public queryPayers = async (query: string): Promise<ProviderPayer[]> => {
        return _.filter(this.payers, payer => _.startsWith(payer.name, query));
    };
    public queryProviders = async (query: string): Promise<ProviderProvider[]> => {
        return _.filter(
            this.providers,
            provider => _.startsWith(provider.first, query) || _.startsWith(provider.last, query),
        );
    };
    public queryGuarantors = async (query: string): Promise<ProviderGuarantor[]> => {
        return _.filter(
            this.guarantors,
            guarantor => _.startsWith(guarantor.first, query) || _.startsWith(guarantor.last, query),
        );
    };
    public queryFacilities = async (query: string): Promise<ProviderFacility[]> => {
        return _.filter(this.facilities, facility => _.startsWith(facility.name, query));
    };
    public querySubscribers = async (query: string): Promise<ProviderSubscriber[]> => {
        return _.filter(
            this.subscribers,
            subscriber => _.startsWith(subscriber.first, query) || _.startsWith(subscriber.last, query),
        );
    };

    public loadMailListing = async (req: ListProviderMailRequest): Promise<IItemListing<ProviderMail>> => {
        await waitForNoReason(400);
        const sorted = sortItems(this.mails, req.sort, req.order);
        return generateItemListing(req, sorted);
    };

    public loadReceiveFaxListing = async (
        req: ListProviderReceiveFaxesRequest,
    ): Promise<IItemListing<ProviderReceiveFax>> => {
        await waitForNoReason(400);
        const sorted = sortItems(this.receiveFaxes, req.sort, req.order);
        return generateItemListing(req, sorted);
    };

    public loadSendFaxListing = async (req: ListProviderSendFaxesRequest): Promise<IItemListing<ProviderSendFax>> => {
        await waitForNoReason(400);
        const sorted = sortItems(this.sendFaxes, req.sort, req.order);
        return generateItemListing(req, sorted);
    };

    public loadCommunicationListing = async (req: ListProviderMailRequest): Promise<IItemListing<ICommunication>> => {
        await waitForNoReason(400);
        const sorted = sortItems(this.communications, req.sort, req.order);
        return generateItemListing(req, sorted);
    };

    public loadLetterListing = async (req: ListProviderLettersRequest): Promise<IItemListing<ProviderLetter>> => {
        await waitForNoReason(400);
        const sorted = sortItems(this.letters, req.sort, req.order);
        return generateItemListing(req, sorted);
    };

    public loadCartListing = async (req: ListProviderMailCartsRequest): Promise<IItemListing<ProviderMailCart>> => {
        await waitForNoReason(400);
        const sorted = sortItems(this.mailCarts, req.sort, req.order);
        return generateItemListing(req, sorted);
    };

    public generateInvoiceLineItem = async (
        invoiceId: string,
        name: string,
        amount: Amount,
    ): Promise<Invoice | undefined> => {
        await waitForNoReason(400);
        const invoice = _.find(this.invoices, { id: invoiceId });
        if (invoice && invoice.items) {
            const item = this.generateInvoiceItem(invoiceId);
            _.set(item, 'name', name);
            _.set(item, 'description', 'User applied item');
            _.set(item, 'discount', amount);
            _.set(item, 'price', undefined);
            _.set(item, 'tax', undefined);
            invoice.items.push(item);
        }
        return invoice;
    };

    public createCollectionPayment = async (
        providerCreateCollectionPaymentPayload: ProviderCreateCollectionPaymentPayload,
    ): Promise<ProviderCollectionPayment | undefined> => {
        await waitForNoReason(400);
        const payment = _.first(
            this.generateCollectionPayments(
                providerCreateCollectionPaymentPayload.claimId || '',
                format(new Date(providerCreateCollectionPaymentPayload.paymentDate), 'yyyy-MM-dd'),
            ),
        );

        return payment;
    };

    public updateClaimGuarantorResponsibility = async (
        claimGuarantorResponsibility: ClaimGuarantorResponsibility,
        claimId: string
    ): Promise<ObjectNoResponse | undefined> => {
        await waitForNoReason(400);
        return new class implements ObjectNoResponse {

        };
    }

    public generateInvoicePaymentPlan = async (
        invoiceId: string,
        amountPerCycle: number,
        intervalCount: number,
        intervalSpan: PaymentPlanIntervalType,
    ): Promise<ProviderPaymentPlan | undefined> => {
        const paymentPlan = this.generatePaymentPlan();
        if (paymentPlan) {
            _.set(paymentPlan, 'invoiceId', invoiceId);
            _.set(paymentPlan, 'amountPerCycle', { amount: String(amountPerCycle), currency: CurrencyType.USD });
            _.set(paymentPlan, 'invoiceId', invoiceId);
            _.set(paymentPlan, 'intervalCount', intervalCount);
            _.set(paymentPlan, 'intervalSpan', intervalSpan);
            this.paymentPlans.push(paymentPlan);
        }
        return paymentPlan;
    };
    public disableInvoicePaymentPlan = async (paymentPlanId: string): Promise<void> => {
        await waitForNoReason(400);
        this.paymentPlans = _.remove(this.paymentPlans, { id: paymentPlanId });
    };

    public generateInvoiceCheckoutUrl = async (invoiceId: string): Promise<SupportShortUrl | undefined> => {
        await waitForNoReason(400);
        return _.first(this.loadFromIndex<SupportShortUrl>('shortUrls', invoiceId));
    };

    public generateGuarantorCheckoutUrl = async (guarantorId: string): Promise<SupportShortUrl | undefined> => {
        await waitForNoReason(400);
        return _.first(this.loadFromIndex<SupportShortUrl>('shortUrls', guarantorId));
    };

    public generateGuarantorClaims = async (guarantorId: string): Promise<ProviderClaim[]> => {
        await waitForNoReason(400);
        return this.claims;
    };

    public generateGuarantorViewBillUrl = async (guarantorId: string): Promise<TemporaryDocumentUrl | undefined> => {
        await waitForNoReason(400);
        return _.first(this.loadFromIndex<TemporaryDocumentUrl>('temporaryDocumentUrls', guarantorId));
    };

    public loadGuarantorBillingDocuments = async (
        claimId: string,
        guarantorId: string,
    ): Promise<ProviderDocument[] | undefined> => {
        await waitForNoReason(400);
        return this.loadFromIndex<ProviderDocument>('documents', guarantorId);
    };

    public loadDocumentById = async (documentId: string): Promise<ProviderDocument | undefined> => {
        await waitForNoReason(400);
        return _.first(this.loadFromIndex<ProviderDocument>('documents', documentId));
    };

    public loadDocumentUrlByDocumentId = async (
        documentId: string,
        documentType?: ProviderDisplayDocumentType,
    ): Promise<ProviderDocumentUrl | undefined> => {
        await waitForNoReason(400);

        if (documentType === ProviderDisplayDocumentType.FAX) {
            return { documentId, url: FaxExample };
        }

        if (documentType === ProviderDisplayDocumentType.CMS1500) {
            return { documentId, url: CMSExample };
        }

        return _.sample<ProviderDocumentUrl>(this.documentUrls);
    };

    public fetchDocumentUrlByPaymentId = async (paymentId: string): Promise<ProviderDocumentUrl | undefined> => {
        await waitForNoReason(400);

        return { ...this.generateTemporaryDocumentUrl(), url: CMSExample };
    };

    public loadMailCartById = async (mailCartId: string): Promise<ProviderMail | undefined> => {
        await waitForNoReason(400);
        return _.find<ProviderMailCart>(this.mailCarts, { id: mailCartId });
    };

    public refundCheckoutCharge = async (
        checkoutCharge: SupportCheckoutCharge,
        memo?: string,
    ): Promise<SupportCheckoutCharge | undefined> => {
        await waitForNoReason(400);
        if (checkoutCharge.id) {
            const refund = this.generateCheckoutChargeRefund(checkoutCharge.id);
            _.set(refund, 'memo', memo);
            _.set(refund, 'refundedAmount', generateAmount(-1 * parseAmount(checkoutCharge.amount)));
            _.set(refund, 'refundedFeeAmount', generateAmount(-1 * parseAmount(checkoutCharge.feeAmount)));
            _.set(refund, 'refundDate', format(new Date(), 'yyyy-MM-dd'));
            return _.assign({}, checkoutCharge, {
                refunds: _.concat([], _.compact(checkoutCharge.refunds), refund),
                status: CheckoutChargeStatusType.REFUNDED,
            });
        }
    };

    public refundCollectionPayment = async (
        paymentId: string,
        supportRefundCollectionPaymentPayload: SupportRefundCollectionPaymentPayload,
    ): Promise<ProviderCollectionPayment | undefined> => {
        await waitForNoReason(400);
        const payment = _.first(
            this.generateCollectionPayments(
                '',
                format(new Date(supportRefundCollectionPaymentPayload.refundDate), 'yyyy-MM-dd'),
            ),
        );

        return { ...payment, refundedAmount: supportRefundCollectionPaymentPayload.refundAmount };
    };

    public loadBillingProviders = async (): Promise<ProviderProvider[] | undefined> => {
        await waitForNoReason(400);
        return _.filter(this.providers, { type: ProviderType.BILLING });
    };

    public loadClaims = async (requests: ListProviderClaimsRequest[]): Promise<ProviderClaim[]> => {
        await waitForNoReason(400);
        return this.claims;
    };

    public loadFacilityById = async (facilityId: string): Promise<ProviderFacility | undefined> => {
        await waitForNoReason(400);
        return _.find(this.facilities, { id: facilityId });
    };

    public loadGuarantorById = async (guarantorId: string): Promise<ProviderGuarantor | undefined> => {
        await waitForNoReason(400);
        return _.find(this.guarantors, { id: guarantorId });
    };
    public loadGuarantors = async (requests: ListProviderGuarantorsRequest[]): Promise<ProviderGuarantor[]> => {
        await waitForNoReason(400);
        return this.guarantors;
    };

    public loadPatientById = async (patientId: string): Promise<ProviderPatient | undefined> => {
        await waitForNoReason(400);
        return _.find(this.patients, { id: patientId });
    };
    public loadPatients = async (requests: ListProviderPatientsRequest[]): Promise<ProviderPatient[]> => {
        await waitForNoReason(400);
        return this.patients;
    };

    public loadPayerById = async (payerId: string): Promise<ProviderPayer | undefined> => {
        await waitForNoReason(400);
        return _.find(this.payers, { id: payerId });
    };
    public loadClaimBundle = async (bundleId: string): Promise<ProviderClaim[]> => {
        await waitForNoReason(400);
        return _.filter(this.claims, claim => claim.claimBundleId === bundleId);
    };
    public loadPayers = async (requests: ListProviderPayersRequest[]): Promise<ProviderPayer[]> => {
        await waitForNoReason(400);
        return this.payers;
    };

    public loadProviderById = async (providerId: string): Promise<ProviderProvider | undefined> => {
        await waitForNoReason(400);
        return _.find(this.providers, { id: providerId });
    };
    public loadProvidersById = async (providerIds: string[]): Promise<ProviderProvider[] | undefined> => {
        await waitForNoReason(400);
        return _.filter(this.providers, provider => _.includes(providerIds, provider.id));
    };
    public loadProviders = async (requests: ListProviderProvidersRequest[]): Promise<ProviderProvider[]> => {
        await waitForNoReason(400);
        return this.providers;
    };

    public loadSellerById = async (sellerId: string): Promise<ProviderSeller | undefined> => {
        await waitForNoReason(400);
        return _.find(this.sellers, { id: sellerId });
    };
    public loadSubscriberById = async (subscriberId: string): Promise<ProviderSubscriber | undefined> => {
        await waitForNoReason(400);
        return _.find(this.subscribers, { id: subscriberId });
    };

    public viewLetterById = async (id: string): Promise<TemporaryDocumentUrl | undefined> => {
        await waitForNoReason(400);
        return _.first(this.loadFromIndex<TemporaryDocumentUrl>('temporaryDocumentUrls', id));
    };

    public viewFaxById = async (id: string): Promise<TemporaryDocumentUrl | undefined> => {
        await waitForNoReason(400);
        return _.first(this.loadFromIndex<TemporaryDocumentUrl>('temporaryDocumentUrls', id));
    };

    private generateEntities(count: number): void {
        // generate a fixed pool of patients, payers, and providers
        _.times(count, () => this.generatePatient());
        _.times(count, i => this.generatePayer(i));
        _.times(count, i => this.generateProvider());

        const numOfClaimsToGenerate = (count * 4);

        //Generate a medicare claim
        const medicarePayer = this.generateSpecificPayer('CIGNA (Medicare) [62308]');
        const medicareDos = d3.utcDay.offset(new Date(), -366);
        this.generateProviderClaim(faker.string.uuid(),{
            payers: [
                medicarePayer
            ],
            fromDos: medicareDos,
            throughDos: medicareDos,
            acceptedPercentageOfCharge: 0.5
        });

        // generate claims
        _.times(numOfClaimsToGenerate - 1, () => this.generateProviderClaim());
    }

    private generatePatient(): ProviderPatient {
        const sex = oneOf(SexType) as string;
        const gender = _.keys(SexType).indexOf(sex);
        const patient = {
            id: faker.string.uuid(),
            first: faker.person.firstName(),
            middle: faker.person.firstName(),
            last: faker.person.lastName(),
            addresses: [generateAddress()],
            pregnant: sex === 'FEMALE' ? _.sample([true, false]) : false,
            dob: format(faker.date.past(), 'yyyy-MM-dd'),
            dod: Math.random() > 0.7 ? format(faker.date.recent(), 'yyyy-MM-dd') : undefined,
            sex: sex as SexType,
            phones: [faker.phone.number()],
            emails: [faker.internet.email()],
            occupation: faker.person.jobTitle(),
            release: _.sample([true, false]),
            created: format(faker.date.past(), 'yyyy-MM-dd'),
            externalId: _.random(500, 10000).toString()
        };
        this.patients.push(patient);
        return patient;
    }

    private generatePayer(i: number): ProviderPayer {
        const payer = {
            id: faker.string.uuid(),
            name: _.get(payerNames, i % _.size(payerNames), 'Unknown'),
            description: '',
            emails: [faker.internet.email()],
            phones: [faker.phone.number()],
            faxes: [faker.phone.number()],
            addresses: [generateAddress()],
            created: format(faker.date.past(), 'yyyy-MM-dd'),
        };
        this.payers.push(payer);
        return payer;
    }

    private generateSpecificPayer(payerName: string): ProviderPayer {
        const payer = {
            id: faker.string.uuid(),
            name: payerName,
            description: '',
            emails: [faker.internet.email()],
            phones: [faker.phone.number()],
            faxes: [faker.phone.number()],
            addresses: [generateAddress()],
            created: format(faker.date.past(), 'yyyy-MM-dd'),
        };
        this.payers.push(payer);
        return payer;
    }

    private generateSubscriber(): ProviderSubscriber {
        const sex = oneOf(SexType);
        const subscriber = {
            id: faker.string.uuid(),
            organization: faker.company.name(),
            first: faker.person.firstName(),
            middle: faker.person.firstName(),
            last: faker.person.lastName(),
            sex,
            phone: faker.phone.number(),
            addresses: [generateAddress()],
            dob: format(faker.date.past(), 'yyyy-MM-dd'),
            ssn: `123-45-6789`,
            created: format(faker.date.past(), 'yyyy-MM-dd'),
        };
        this.subscribers.push(subscriber);
        return subscriber;
    }

    private generatePayerSubscriber(subscriber: ProviderSubscriber, providedPayer?: ProviderPayer|undefined): ProviderPayerSubscriber {
        const payer = providedPayer ? providedPayer : _.sample(this.payers) as ProviderPayer;
        const payerSubscriber = {
            id: faker.string.uuid(),
            memberId: `${_.random(1e6, 1e7)}`,
            groupId: `${_.toUpper(faker.string.alphanumeric(2))}-${_.random(1e7, 1e8)}`,
            groupName: faker.commerce.department(),
            employerName: faker.company.name(),
            medicareType: oneOf(MedicareType),
            planName: faker.lorem.sentence(),
            uniqueId: _.uniqueId('UniqueId'),
            subscriber,
            payer,
        };
        this.payerSubscribers.push(payerSubscriber);
        return payerSubscriber;
    }

    private generateSubscriberPatient(subscriber: ProviderSubscriber): ProviderSubscriberPatient {
        const patient = _.sample(this.patients) as ProviderPatient;
        const subscriberPatient = {
            id: faker.string.uuid(),
            relationship: oneOf(RelationshipType),
            subscriber,
            patient,
        };
        this.subscriberPatients.push(subscriberPatient);
        return subscriberPatient;
    }

    private generateSeller(): ProviderSeller {
        const seller = {
            id: faker.string.uuid(),
            externalId: String(_.random(1e6, 1e9)),
            organization: faker.company.name(),
            first: faker.person.firstName(),
            last: faker.person.lastName(),
            suffix: faker.person.suffix(),
            created: format(faker.date.past(), 'yyyy-MM-dd'),
        };
        this.sellers.push(seller);
        return seller;
    }

    private generateFacility(): ProviderFacility {
        const facility = {
            id: faker.string.uuid(),
            name: faker.company.name(),
            description: faker.lorem.paragraph(),
            addresses: [generateAddress()],
            npi: String(_.random(1e6, 1e9)),
            ein: String(_.random(1e6, 1e9)),
            ssn: String(_.random(1e6, 1e9)),
            other: String(_.random(1e6, 1e9)),
            created: format(faker.date.past(), 'yyyy-MM-dd'),
        };
        this.facilities.push(facility);
        return facility;
    }


    private generateProviderClaim(claimId: string = faker.string.uuid(), claimGeneratorOptions?: ClaimGeneratorClaimOptions | undefined): ProviderClaim {
        // NB: generate dates for the claim:
        // fromDos - random date in [startDate, endDate]
        // throughDos - random date in [fromDos, fromDos + rand(1, 30)]
        const fromDos = (claimGeneratorOptions && claimGeneratorOptions.fromDos) ? claimGeneratorOptions.fromDos : faker.date.between({ from: this.startDate, to: this.endDate });
        const throughDos = (claimGeneratorOptions && claimGeneratorOptions.throughDos) ? claimGeneratorOptions.throughDos : this.recentDateAfter(fromDos, 1, 30);

        // build professional claim first, including dependent models
        const professionalClaim = this.generateProfessionalClaim(claimId, this.recentDateAfter(fromDos, 1), claimGeneratorOptions);
        this.storeToIndex('professionalClaims', claimId, professionalClaim);

        const { guarantor, patients, payers, payerSubscribers } = this.resolveProfessionalClaim(professionalClaim);
        const patient = _.first(patients) as ProviderPatient;
        const payer = _.first(payers) as ProviderPayer;

        const chargeAmount = _.random(500, 10000);
        // payerAmount + patientAmount;
        const allowedAmount = Math.floor(chargeAmount / 1.5);
        const payerAmount = Math.floor(allowedAmount * _.random(0.2, 1));
        const patientAmount = Math.floor(payerAmount * _.random(0, 0.8));
        const patientInvoicePaidAmount = Math.floor(patientAmount * _.random(0, 1));
        const patientPrepaidAmount = _.sample([20, 35, 40]);

        const claim: ProviderClaim = {
            created: this.recentDateAfter(fromDos, 1),
            id: claimId,
            fromDos: format(fromDos, 'yyyy-MM-dd'),
            throughDos: format(throughDos, 'yyyy-MM-dd'),
            guarantorId: guarantor?.id,
            guarantorFirst: guarantor?.first,
            guarantorLast: guarantor?.last,
            patientFirst: patient.first,
            patientLast: patient.last,
            primaryPayerName: payer.name,
            claimResponsibilityType: oneOf(ClaimResponsibilityType),
            chargeAmount: generateAmount(chargeAmount),
            payerAmount: generateAmount(payerAmount),
            allowedAmount: generateAmount(allowedAmount),
            patientAmount: generateAmount(patientAmount),
            chargemasterAllowedAmount: generateAmount(allowedAmount),
            patientInvoiceAmount: generateAmount(patientAmount),
            patientInvoicePaidAmount: generateAmount(patientInvoicePaidAmount),
            type: ClaimType.PROFESSIONAL,
            collectionStatus: ProviderCollectionStatusType.PATIENTCOLLECTIONSENT
        };
        this.claims.push(claim);

        // document models
        const document: ProviderDocument = this.generateDocument(claimId, this.recentDateAfter(fromDos, 3));
        this.storeToIndex('documents', claimId, document);
        this.storeToIndex(
            'documentUrls',
            claimId,
            upToNTimes(() => this.generateDocumentUrl(document.id), 2),
        );

        // TODO: randomize number of letter/mail/fax per document
        this.storeToIndex('letters', claimId, this.generateLetter(document));
        this.storeToIndex('mails', claimId, this.generateMail(document));
        this.storeToIndex('receiveFaxes', claimId, this.generateReceiveFax(document));
        this.storeToIndex('sendFaxes', claimId, this.generateSendFax(document));


        const claimPayments = this.generateClaimPayments(claim, this.recentDateAfter(fromDos, 10));
        const patientResponsibility = this.getPatientResponsibilityFromClaimPayments(claimPayments);

        const collectionPaymentDate = this.recentDateAfter(fromDos, 6);
        const collectionPaymentPaid = generateAmount(parseAmount(patientResponsibility) * 0.2);

        const claimChargeAmount = this.getClaimChargeAmountFromClaim(claim);
        let claimPayerAmount = 0;
        let claimAllowedAmount = 0;

        for(const claimPayment of claimPayments) {
            claimPayerAmount+= parseAmount(claimPayment.payerAmount);
            claimAllowedAmount+= parseAmount(claimPayment.allowedAmount);
        }


        claim.patientInvoicePaidAmount = collectionPaymentPaid;
        claim.patientInvoiceAmount = patientResponsibility;
        claim.chargeAmount = claimChargeAmount;
        claim.allowedAmount = generateAmount(claimAllowedAmount);
        claim.payerAmount = generateAmount(claimPayerAmount);
        claim.chargemasterAllowedAmount = this.getChargemasterAcceptedAmountFromClaim(claim);

        const collectionPayments = [{
            id: faker.string.uuid(),
            collectedAmount: collectionPaymentPaid,
            feeAmount: generateAmount(parseAmount(collectionPaymentPaid) * 0.05),
            claimId,
            paymentDate: this.recentDateAfter(collectionPaymentDate, 0, 5),
            created: collectionPaymentDate,
        }];

        this.storeToIndex(
            'collections',
            claimId,
            collectionPayments,
        );

        /*
                // related models
        this.storeToIndex(
            'collections',
            claimId,
            this.generateCollectionPayments(claimId, this.recentDateAfter(fromDos, 6)),
        );
         */

        this.storeToIndex('eligibilities', claimId, this.generateEligibility(this.recentDateAfter(fromDos, 4)));
        this.storeToIndex('hl7Messages', claimId, this.generateHl7Message(claimId, format(fromDos, 'yyyy-MM-dd')));
        this.storeToIndex('payments', claimId, claimPayments);
        this.storeToIndex('statuses', claimId, this.generateClaimStatus(claimId, fromDos));
        this.storeToIndex('transactions', claimId, this.generateTransaction(fromDos));
        this.storeToIndex('invoices', claimId, this.generateInvoice(fromDos, InvoiceType.PATIENT, patientResponsibility, collectionPaymentPaid)); // force a patient invoice
        this.storeToIndex('invoices', claimId, this.generateInvoice(fromDos));

        this.storeToIndex(
            'transmissions',
            claimId,
            this.generateTransmission(this.recentDateAfter(fromDos, 5), _.first(payerSubscribers)),
        );

        return claim;
    }
    private generateProfessionalClaim = (claimId: string, date: string, claimGeneratorOptions?: ClaimGeneratorClaimOptions|undefined): ProviderProfessionalClaim => {
        const { ...medicalProps } = this.generateMedicalClaim(claimId, date, claimGeneratorOptions);
        const professionalTemplate = this.generateProfessionalTemplate(claimId, date, claimGeneratorOptions);
        this.storeToIndex('professionalTemplates', claimId, professionalTemplate);
        return {
            id: faker.string.uuid(),
            created: format(date, 'yyyy-MM-dd'),
            professionalTemplateId: professionalTemplate.id,
            ...medicalProps,
        };
    };
    private generateMedicalClaim = (claimId: string, date: string, claimGeneratorOptions?: ClaimGeneratorClaimOptions|undefined): ProviderMedicalClaim => {
        const guarantor = this.generateGuarantor(claimId, date);
        const subscriber = this.generateSubscriber();
        let payerSubscribers = [];
        if(claimGeneratorOptions && claimGeneratorOptions.payers) {
            for(const providedPayer of claimGeneratorOptions.payers) {
                payerSubscribers.push(this.generatePayerSubscriber(subscriber, providedPayer));
            }
        } else {
            payerSubscribers = _.times(_.random(1, 2), () => this.generatePayerSubscriber(subscriber))
        }
        const subscriberPatients = _.times(_.random(1, 2), () => this.generateSubscriberPatient(subscriber));
        const providers = _.uniq(_.times(_.random(1, 3), () => _.sample(this.providers)));
        const seller = this.generateSeller();
        const facility = this.generateFacility();

        if (_.sample([true, false])) {
            const eligibility = this.generateEligibility(this.recentDateAfter(date, 4));
            eligibility.groupId = _.sample(payerSubscribers)?.groupId;
            this.storeToIndex('eligibilities', claimId, eligibility);
        }

        return {
            guarantorId: _.get(guarantor, 'id', ''),
            chargemasterId: _.uniqueId('chargemaster'),
            payerSubscriberIds: _.compact(_.map(payerSubscribers, 'id')),
            subscriberPatientIds: _.compact(_.map(subscriberPatients, 'id')),
            providerIds: _.compact(_.map(providers, 'id')),
            sellerId: _.get(seller, 'id', ''),
            facilityId: _.get(facility, 'id', ''),
            claimId,
        };
    };
    private generateProfessionalTemplate = (claimId: string, date: string, claimGeneratorOptions?: ClaimGeneratorClaimOptions|undefined): ProviderProfessionalClaimTemplate => {
        const serviceLines = upToNTimes(i => this.generateProfessionalTemplateServiceLine(claimId, date, i, claimGeneratorOptions), 9);
        return {
            created: date,
            id: faker.string.uuid(),
            name: faker.lorem.sentence(),
            description: faker.lorem.sentence(),
            f19ClaimNarrative: faker.lorem.sentence(),
            f21aDiagnosisCode: faker.lorem.sentence(),
            f24ServiceLine: serviceLines,
            f26PatientAccountNumber: _.uniqueId('patient-account-number'),
            f28TotalCharge: generateAmount(),
            f29AmountPaid: generateAmount(),
            f30Balance: generateAmount(),
        };
    };
    private generateProfessionalTemplateServiceLine = (
        claimId: string,
        date: string,
        index: number | undefined,
        claimGeneratorOptions?: ClaimGeneratorClaimOptions|undefined
    ): SupportProfessionalTemplateServiceLine => {
        const chargemasterItem = this.generateChargemasterItem(claimId, claimGeneratorOptions);
        const line = {
            f24aFromDateOfService: this.recentDateAfter(date),
            f24bPlaceOfServiceCode: oneOf(PlaceOfServiceType),
            f24dProcedureCode: chargemasterItem.procedure as string,
            f24dProcedureModifiers: chargemasterItem.modifiers,
            f24eDiagnosisPointers: [faker.lorem.word()],
            f24fChargeAmount: chargemasterItem.amount,
            f24gUnits: 1, //_.random(1, 10)
            chargemasterItemId: chargemasterItem.id,
        };

        _.assign(line, {
            x12Narrative: this.generateX12Narrative(),
            x12ControlNumber: this.generateControlNumber(index),
            x12Note: faker.lorem.sentences(),
            x12ProviderIds: upToNTimes(() => _.uniqueId('provider'), 3),
            x12FacilityId: _.uniqueId('facility'),
            x12DurableMedicalEquipment: this.generateX12DurableMedicalEquipment(),
            x12TestResults: this.generateX12TestResults(),
            x12Paperwork: this.generateX12Paperwork(),
            x12Ambulance: this.generateX12Ambulance(),
            x12Anesthesia: this.generateX12Anesthesia(),
            x12Drug: this.generateX12Drug(),
            x12HospiceEmployee: _.sample([true, false, undefined]),
            x12Forms: this.generateX12Forms(),
        });

        // // Only add X12 data to every other line
        // if (!_.isNil(index) && index % 2 === 0) {
        //     _.assign(line, {
        //         x12Narrative: this.generateX12Narrative(),
        //         x12ControlNumber: this.generateControlNumber(),
        //         x12Note: faker.lorem.sentences(),
        //         x12ProviderIds: upToNTimes(() => _.uniqueId('provider'), 3),
        //         x12FacilityId: _.uniqueId('facility'),
        //         x12DurableMedicalEquipment: this.generateX12DurableMedicalEquipment(),
        //         x12TestResults: this.generateX12TestResults(),
        //         x12Paperwork: this.generateX12Paperwork(),
        //         x12Ambulance: this.generateX12Ambulance(),
        //         x12Anesthesia: this.generateX12Anesthesia(),
        //         x12Drug: this.generateX12Drug(),
        //         x12HospiceEmployee: _.sample([true, false, undefined]),
        //         x12Forms: this.generateX12Forms(),
        //     });
        // }
        return line;
    };
    private generateChargemasterItem = (claimId: string, claimGeneratorOptions?: ClaimGeneratorClaimOptions|undefined): ProviderChargemasterItem => {

        const item = {
            id: _.uniqueId('chargemasterItem'),
            procedure: this.generateProcedureCode(),
            modifiers: upToNTimes(this.generateProcedureModifier, 3),
            amount: generateAmount(),
            allowed: generateAmount(),
            expected: generateAmount(),
            accepted: generateAmount(),
            patient: generateAmount(),
            medicare: generateAmount()
        };

        /*
        const max = parseAmount(claim.chargeAmount) / numPayments;
            const payerAmount = _.random(1, max);
            const patientAmount = _.random(1, max - payerAmount);
            const allowedAmount = _.random(1, 0);
         */

        if(claimGeneratorOptions && claimGeneratorOptions.acceptedPercentageOfCharge) {
            item.accepted = generateAmount(parseAmount(item.amount) * claimGeneratorOptions.acceptedPercentageOfCharge);
        } else {
            item.accepted = generateAmount(parseAmount(item.amount) * 0.75);
        }

        item.medicare = generateAmount(parseAmount(item.amount) * 0.5);

        this.storeToIndex('chargemasterItems', claimId, item);
        return item;
    };
    private generateProcedureCode = (): string => {
        return _.sample(['G0378', '77330', '74177', '76705', '85025', '80053', '81022']) as string;
    };
    private generateProcedureModifier = (): string => {
        return _.sample(['TC', 'P1', 'P2', 'P3', 'P4', 'QW']) as string;
    };
    private generateX12Narrative = (): string | undefined => {
        return _.sample<string>([
            'Hospital observation service, per hour',
            'CT abdomen and pelvis; with contrast',
            'Echo exam of abdomen',
            'Automated hemogram CBC',
            'Fluids administered',
            'Held for observation',
            'MRI scan',
        ]);
    };

    generatedControlNumbers: string[] = [
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
        _.random(1, 99999999).toString(),
    ]

    private generateControlNumber = (index:number=-1): string => {
        return this.generatedControlNumbers[index == -1 ? _.random(0, 10) : index];
    };
    private generateX12DurableMedicalEquipment = ():
        | ProfessionalTemplateDurableMedicalEquipmentInformation
        | undefined => {
        return {
            x12LengthOfMedicalNecessity: _.random(1, 10),
            x12RentalPrice: generateAmount(),
            x12PurchasePrice: generateAmount(),
            x12RentalUnitPriceIndicatorCode: oneOf(DMERentalUnitPriceIndicatorCodeType),
            x12ReportTransmissionCode: oneOf(DMEReportTransmissionCodeType),
            x12CertificationCode: oneOf(DMECertificationTypeCode),
            x12CertificationDuration: _.random(1, 30),
            x12PrimaryConditionIndicator: oneOf(DMEConditionIndicatorCodeType),
            x12SecondaryConditionIndicator: oneOf(DMEConditionIndicatorCodeType),
            x12CertificationRevisionDate: this.recentDate(),
            x12BeginTherapyDate: this.recentDate(),
            x12LastCertificationDate: this.recentDate(),
        };
    };
    private generateX12TestResults = (): ProfessionalTemplateTestResult[] | undefined => {
        return upToNTimes(
            () => ({
                x12TestResultIdentificationCode: oneOf(TestResultIdentificationCode),
                x12TestResultMeasurementQualifier: oneOf(TestResultMeasurementQualifier),
                x12TestResultMeasurementValue: faker.lorem.word(),
            }),
            3,
        );
    };
    private generateX12Paperwork = (): ProfessionalTemplatePaperwork[] | undefined => {
        return upToNTimes(
            () => ({
                x12PaperworkReportCode: oneOf(PaperworkReportCode),
                x12PaperworkTransmissionCode: oneOf(PaperworkTransmissionCode),
                x12PaperworkControlNumber: _.uniqueId('controlNumber'),
            }),
            4,
        );
    };
    private generateX12Ambulance = (): ProfessionalTemplateAmbulanceInformation | undefined => {
        return {
            x12PatientWeight: _.random(100, 450),
            x12TransportReasonCode: oneOf(AmbulanceTransportReasonCodeType),
            x12Distance: _.random(1, 100),
            x12RoundTripNarrative: faker.lorem.sentence(),
            x12StretcherNarrative: faker.lorem.sentence(),
            x12Certifications: upToNTimes(
                () => ({
                    x12ConditionCodes: subsetOf(AmbulanceCertificationConditionCode, _.random(1, 3)),
                }),
                2,
            ),
            x12PickUpLocation: generateAddress(),
            x12DropOffLocation: generateAddress(),
            x12PatientCount: _.random(1, 4),
        };
    };
    private generateX12Anesthesia = (): ProfessionalTemplateAnesthesiaInformation | undefined => {
        return {
            x12Minutes: _.random(1, 200),
            x12ObstetricUnits: _.random(1, 100),
        };
    };
    private generateX12Drug = (): ProfessionalTemplateDrugInformation | undefined => {
        return {
            x12NationalDrugCode: faker.lorem.word(),
            x12DrugQuantity: String(_.random(1, 50)),
            x12DrugMeasurement: oneOf(DrugMeasurementCode),
        };
    };
    private generateX12Forms = (): ProfessionalTemplateForm[] | undefined => {
        return upToNTimes(
            () => ({
                x12FormId: _.uniqueId('formId'),
                x12FormType: oneOf(FormType),
                x12FormResponses: upToNTimes(
                    i => ({
                        x12QuestionNumber: String(i),
                        x12ResponseBoolean: faker.datatype.boolean(),
                        x12ResponseText: faker.lorem.sentence(),
                        x12ResponseDate: this.recentDate(),
                        x12ResponseNumber: _.random(1, 100),
                    }),
                    3,
                ),
            }),
            4,
        );
    };
    private generateClaimStatus = (claimId: string, date: Date): ProviderStatus => {
        const created = this.recentDateAfter(date);
        return {
            created: format(created, 'yyyy-MM-dd'),
            id: faker.string.uuid(),
            items: _.times(_.random(1, 5), () => this.generateClaimStatusItem(date)),
        };
    };
    private generateClaimStatusItem = (date: Date): StatusItem => {
        // const remittanceDate = this.recentDateAfter(date);
        // const finalizedDate = this.recentDateAfter(date);
        // const serviceFromDate = this.recentDateAfter(date);
        // const serviceThroughDate = this.recentDateAfter(serviceFromDate);
        // const statusEffectiveDate = this.recentDateAfter(date);
        return {
            // pcn: _.uniqueId('pcn'),
            // ccn: _.uniqueId('ccn'),
            // scn: _.uniqueId('scn'),
            // chargeAmount: generateAmount(_.random(1, 10000)),
            // payerAmount: generateAmount(_.random(1, 10000)),
            // units: String(_.random(1, 9)),
            // remittanceDate,
            // finalizedDate,
            // serviceFromDate,
            // serviceThroughDate,
            // procedureCode: _.uniqueId('procedureCode'),
            // statuses: _.times(_.random(1, 3), this.generateClaimStatusItemCode),
            // statusEffectiveDate,
            // checkNumber: _.uniqueId('checkNumber'),
        };
    };
    // private generateClaimStatusItemCode = (): StatusItemCode => {
    //     return {
    //         statusCode: _.uniqueId('statusCode'),
    //         statusCategoryCode: _.uniqueId('statusCategoryCode'),
    //         payerResponsibilityType: oneOf(PayerResponsibilityType),
    //     };
    // };

    private generateCollectionPayments = (claimId: string, date: string): ProviderCollectionPayment[] => {
        const numPayments = _.random(1, 2);
        return _.times(numPayments, () => {
            return {
                id: faker.string.uuid(),
                collectedAmount: generateAmount(),
                feeAmount: generateAmount(),
                claimId,
                paymentDate: this.recentDateAfter(date, 0, 5),
                created: date,
            };
        });
    };

    private getPatientResponsibilityFromClaimPayments = (claimPayments: ProviderClaimPayment[]): Amount => {
        let prAmount = 0;
        for(const claimPayment of claimPayments) {
            if(claimPayment.items) {
                for(const item of claimPayment.items) {
                    if(item.adjustments) {
                        for (const adjustment of item.adjustments) {
                            if (adjustment.amount && adjustment.group && adjustment.group === PayerAdjustmentGroupType.PATIENTRESPONSIBILITY) {
                                prAmount += parseAmount(adjustment.amount);
                            }
                        }
                    }
                }

            }
        }
        return generateAmount(prAmount);
    }

    private getChargemasterAcceptedAmountFromClaim = (claim: ProviderClaim): Amount => {
        let acceptedAmount: number = 0;
        const professionalClaims = this.loadFromIndex<ProviderProfessionalClaim>('professionalClaims', claim.id!);
        const professionalTemplates = this.loadFromIndex<ProviderProfessionalClaimTemplate>(
            'professionalTemplates',
            claim.id!
        );
        const professionalClaim = _.first<ProviderProfessionalClaim>(professionalClaims);
        const professionalTemplate = _.first<ProviderProfessionalClaimTemplate>(professionalTemplates);

        if(professionalTemplate) {
            for(const professionalTemplateServiceLine of professionalTemplate.f24ServiceLine) {
                const chargemasterItem = _.find([...(this.chargemasterItems)], {
                    id: professionalTemplateServiceLine.chargemasterItemId,
                });
                if(chargemasterItem) {
                    acceptedAmount+= parseAmount(chargemasterItem!.accepted);
                }
            }
        }


        return generateAmount(acceptedAmount);
    }

    private getClaimChargeAmountFromClaim = (claim: ProviderClaim): Amount => {
        let chargeAmount: number = 0;
        const professionalClaims = this.loadFromIndex<ProviderProfessionalClaim>('professionalClaims', claim.id!);
        const professionalTemplates = this.loadFromIndex<ProviderProfessionalClaimTemplate>(
            'professionalTemplates',
            claim.id!
        );
        const professionalClaim = _.first<ProviderProfessionalClaim>(professionalClaims);
        const professionalTemplate = _.first<ProviderProfessionalClaimTemplate>(professionalTemplates);

        if(professionalTemplate) {
            for(const professionalTemplateServiceLine of professionalTemplate.f24ServiceLine) {
                const chargemasterItem = _.find([...(this.chargemasterItems)], {
                    id: professionalTemplateServiceLine.chargemasterItemId,
                });
                if(chargemasterItem) {
                    chargeAmount+= parseAmount(chargemasterItem.amount);
                }
            }
        }


        return generateAmount(chargeAmount);
    }

    private getPatientResponsibilityFromClaimPaymentItems = (items: ProviderClaimPaymentItem[]): Amount => {
        let prAmount = 0;
        for(const item of items) {
            if(item.adjustments) {
                for (const adjustment of item.adjustments) {
                    if (adjustment.amount && adjustment.group && adjustment.group === PayerAdjustmentGroupType.PATIENTRESPONSIBILITY) {
                        prAmount += parseAmount(adjustment.amount);
                    }
                }
            }
        }
        return generateAmount(prAmount);
    }

    private generateClaimPayments = (claim: ProviderClaim, date: string): ProviderClaimPayment[] => {
        const numPayments = 1; //_.random(1, 4);

        // dereferenced
        const professionalClaims = this.loadFromIndex<ProviderProfessionalClaim>('professionalClaims', claim.id!);
        const professionalTemplates = this.loadFromIndex<ProviderProfessionalClaimTemplate>(
            'professionalTemplates',
            claim.id!
        );
        const professionalClaim = _.first<ProviderProfessionalClaim>(professionalClaims);
        const professionalTemplate = _.first<ProviderProfessionalClaimTemplate>(professionalTemplates);

        return _.times(numPayments, () => {
            const createdDate = new Date(date);
            const paymentDate = this.recentDateAfter(createdDate);
            const receivedDate = this.recentDateAfter(createdDate);
            const payerControlNumber = this.generateControlNumber();
            const findPindId = (key: string): string | undefined => {
                const list = this.loadFromIndex(key, claim.id as string);
                return _.get(list, '[0].id');
            };

            const paymentItems = this.generateClaimPaymentItems(payerControlNumber, createdDate, professionalTemplate!);
            const patientResponsibility = this.getPatientResponsibilityFromClaimPaymentItems(paymentItems);
            let chargeAmount = 0;
            let allowedAmount = 0;
            const patientAmount = patientResponsibility;
            let payerAmount = 0;

            for(const pi of paymentItems) {
                chargeAmount+= parseAmount(pi.chargeAmount);
                allowedAmount+= parseAmount(pi.allowedAmount);
                payerAmount+= parseAmount(pi.paidAmount);
            }

            //const max = parseAmount(claim.chargeAmount) / numPayments;
            //const payerAmount = _.random(1, max);
            //const patientAmount = _.random(1, max - payerAmount);
            //const allowedAmount = _.random(1, 0);
            //const chargeAmount = allowedAmount + payerAmount + patientAmount;

            return {
                created: format(createdDate, 'yyyy-MM-dd'),
                id: faker.string.uuid(),
                chargeAmount: generateAmount(chargeAmount),
                allowedAmount: generateAmount(allowedAmount),
                payerAmount: generateAmount(payerAmount),
                patientAmount: patientAmount,
                payerControlNumber,
                status: PaymentStatusType.PROCESSEDASPRIMARY, //oneOf(PaymentStatusType)
                paymentDate,
                receivedDate,
                items: paymentItems,
                professionalId: claim.type === ClaimType.PROFESSIONAL ? findPindId('professionalClaims') : undefined,
                institutionalId: claim.type === ClaimType.INSTITUTIONAL ? findPindId('institutionalClaims') : undefined,
                dentalId: claim.type === ClaimType.DENTAL ? claim.id : undefined,
                nynf3Id: claim.type === ClaimType.NYNF3 ? claim.id : undefined,
            };
        });
    };
    private generateClaimPaymentItems = (controlNumber: string, paymentDate: Date, professionalTemplate: ProviderProfessionalClaimTemplate): ProviderClaimPaymentItem[] => {
        const retPaymentItems = [];

        for(const professionalTemplateServiceLine of professionalTemplate.f24ServiceLine) {
            const chargemasterItem = _.find([...(this.chargemasterItems)], {
                id: professionalTemplateServiceLine.chargemasterItemId,
            });

            const x12ControlNumber = professionalTemplateServiceLine.x12ControlNumber;


            //Scenario: Allowed amount = accepted amount
            const allowedAmount = chargemasterItem!.accepted;

            //Paid amount will be random 45%-95%
            const paidAmount = generateAmount(parseAmount(allowedAmount) * _.random(0.45, 0.95, true));


            //Everything else goes to patient responsibility
            const adjustments = [{
                group: PayerAdjustmentGroupType.PATIENTRESPONSIBILITY,
                reasonCode: '1',
                amount: generateAmount(parseAmount(allowedAmount) - parseAmount(paidAmount)),
                units: 1,
            },
            {
                group: PayerAdjustmentGroupType.CONTRACTUALOBLIGATIONS,
                reasonCode: '45',
                amount: generateAmount(parseAmount(chargemasterItem!.amount) - parseAmount(allowedAmount)),
                units: 1,
            }];

            /*
            45-95%
             */

            const fromDos = this.recentDateAfter(paymentDate);
            const throughDos = this.recentDateAfter(fromDos);
            const paymentItem = {
                controlNumber: x12ControlNumber,
                procedureCode: professionalTemplateServiceLine.f24dProcedureCode,
                modifierCodes: professionalTemplateServiceLine.f24dProcedureModifiers,
                remarkCodes: _.times(_.random(1, 4), () => _.uniqueId('remarkCode')),
                chargeAmount: chargemasterItem!.amount, //generateAmount(_.random(1, 10000))
                chargeCount: 1, //x12ControlNumber
                allowedAmount: allowedAmount, //generateAmount(_.random(1, 10000))
                paidAmount: paidAmount, //generateAmount(_.random(1, 10000))
                paidCount: 1,
                renderingProviderId: _.uniqueId('providerId'),
                fromDos,
                throughDos,
                placeOfServiceCode: oneOf(PlaceOfServiceType),
                adjustments: adjustments //this.generateClaimPaymentItemAdjustments(),
            };
            retPaymentItems.push(paymentItem);
        }

        return retPaymentItems;
    };
    private generateClaimPaymentItemAdjustments = (): ProviderClaimPaymentItemAdjustment[] => {
        return _.times(_.random(0, 4), () => ({
            group: oneOf(PayerAdjustmentGroupType),
            reasonCode: _.uniqueId('reasonCode'),
            amount: generateAmount(_.random(1, 1000)),
            units: 1,
        }));
    };

    private generateDocument = (claimId: string, date: string): ProviderDocument => {
        return {
            created: date,
            id: faker.string.uuid(),
            filename: faker.system.fileName(),
            description: faker.system.semver(),
            claimId,
            documentType: oneOf(ProviderDisplayDocumentType),
        };
    };

    private generateDocumentUrl = (documentId: string = faker.string.uuid()): ProviderDocumentUrl => {
        return {
            documentId,
            url: CMSExample,
        };
    };

    private generateEligibility = (date: string): ProviderClaimEligibility => {
        return {
            created: date,
            id: faker.string.uuid(),
            planId: String(_.random(1e6, 1e9)),
            planDescription: faker.lorem.sentence(),
            groupId: String(_.random(1e6, 1e9)),
            groupDescription: faker.lorem.sentence(),
            otherQualifierId: String(_.random(1e6, 1e9)),
            otherId: String(_.random(1e6, 1e9)),
            otherDescription: faker.lorem.sentence(),
            planStartDate: date,
            planEndDate: date,
            items: upToNTimes(() => this.generateEligibilityItem(), 5),
        };
    };

    private generateHl7Message = (claimId: string, date: string): SupportHL7Message => {
        const message =
            'MSH|^~\\&|pMDsoft|7484|Enter Health||20200412084033|6c4953642f34|DFT^P03|3875621673|P|2.3.1||||AL|\rEVN|P03|20200412084033|\rPID||1111111111|||DOE^JANE^UNIDENTIFIED||19840318|M|\rPV1||O|SI||||1093057200^Doe^John^Unidentified^^^^^&1093057200&NPI||||||||||||725320003||||||||||||||||||||SI|||^^^SI||20200331|\rFT1|1|725320003-1||20200409000000|20200409|CG|99233|||1||||||SI^Siena|||R19.00^Abd/Pelv Mass^I10~R10.84^Pain Abd Generalized^I10~K59.00^Constipation Nos^I10~G89.4^Pain Chronic Syndrome^I10~E86.0^Hypovolemia/Dehydration^I10~K56.609^Unsp intestnl obst, unsp as to partial versus complete obst^I10~K56.7^Ileus, unspecified^I10|1093057200^Doe^John^Unidentified^^^^^&1093057200&NPI|||||99233^Hosp FollowUp 3 High FU|\n';
        return {
            id: faker.string.uuid(),
            message,
            claimId,
            created: date,
        };
    };

    private generateEligibilityItem = (): ClaimEligibilityItem => {
        const from = faker.date.recent();
        return {
            type: oneOf(EligibilityItemType),
            coverageLevelType: oneOf(CoverageLevelType),
            planType: oneOf(PlanType),
            coverageDescription: faker.lorem.sentence(),
            timePeriodType: oneOf(TimePeriodType),
            benefitAmount: generateAmount(),
            benefitPercentage: `${_.random(1, 50)}%`,
            quantityType: oneOf(QuantityType),
            quantity: String(_.random(1, 4)),
            authorizationRequiredType: oneOf(AuthorizationRequiredType),
            inPlanNetworkType: oneOf(InPlanNetworkType),
            procedureCodeType: oneOf(ProcedureCodeType),
            fromProcedureCode: faker.lorem.word(),
            throughProcedureCode: faker.lorem.word(),
            modifierCode1: faker.lorem.word(),
            modifierCode2: faker.lorem.word(),
            modifierCode3: faker.lorem.word(),
            modifierCode4: faker.lorem.word(),
            diagnosisPointer1: faker.lorem.word(),
            diagnosisPointer2: faker.lorem.word(),
            diagnosisPointer3: faker.lorem.word(),
            diagnosisPointer4: faker.lorem.word(),
            eligibilityFromDate: format(from, 'yyyy-MM-dd'),
            eligibilityThroughDate: this.recentDateAfter(from),
            serviceTypeCodes: upToNTimes(() => oneOf(ServiceType), 4),
            messages: upToNTimes(() => faker.lorem.sentence(), 1, 3),
        };
    };

    private generateInvoice = (date: Date, invoiceType: InvoiceType = oneOf(InvoiceType), patientResponsibility: Amount|undefined = undefined, patientPaid: Amount|undefined = undefined): Invoice => {
        const amount = patientResponsibility ? parseAmount(patientResponsibility) : _.random(1, 1000);
        const paid = patientPaid ? parseAmount(patientPaid) : _.random(1, amount);
        const remaining = amount - paid;
        const invoiceId = faker.string.uuid();
        const invoice = {
            created: this.recentDateAfter(date),
            id: invoiceId,
            description: faker.lorem.sentence(),
            memo: faker.lorem.sentence(),
            amount: generateAmount(amount),
            paidAmount: generateAmount(paid),
            remainingAmountDue: generateAmount(remaining),
            invoiceDate: format(faker.date.past(), 'yyyy-MM-dd'),
            groupId: _.uniqueId('group'),
            latestResourceInGroup: true,
            invoiceType,
            invoiceStatus: oneOf(InvoiceStatusType),
            items: upToNTimes(() => this.generateInvoiceItem(invoiceId), 3),
        };
        const paymentPlan = this.generatePaymentPlan();
        _.set(paymentPlan, 'invoiceId', invoiceId);
        this.storeToIndex('paymentPlans', invoiceId, paymentPlan);
        const url = this.generateSupportShortUrl();
        this.storeToIndex('shortUrls', invoiceId, url);
        return invoice;
    };

    private generateInvoiceItem = (invoiceId: string): InvoiceItem => {
        const price = _.random(1, 1000);
        return {
            id: faker.string.uuid(),
            itemId: invoiceId,
            name: faker.lorem.slug(),
            description: faker.lorem.sentence(),
            quantity: faker.number.int({ max: 3 }),
            price: generateAmount(price),
            discount: generateAmount(-1 * _.random(1, price)),
            tax: generateAmount(price / 10),
        };
    };

    private generateSupportShortUrl = (): SupportShortUrl => {
        return {
            id: faker.string.uuid(),
            url: faker.internet.url(),
            urlType: oneOf(ShortUrlType),
        };
    };

    private generateTemporaryDocumentUrl = (): TemporaryDocumentUrl => {
        return {
            temporaryDocumentId: faker.string.uuid(),
            url: faker.internet.url(),
        };
    };

    private generateLetter = (document: ProviderDocument): ProviderLetter => {
        const createdDate = document.created ? new Date(document.created) : faker.date.past();
        return {
            created: format(createdDate, 'yyyy-MM-dd'),
            id: faker.string.uuid(),
            cancelled: faker.datatype.boolean(),
            response: this.generateLetterResponse(createdDate),
            document,
            flowId: _.uniqueId('flowId'),
            letterTemplateId: _.uniqueId('letterTemplateId'),
        };
    };

    private generateLetterResponse = (createdDate: Date): SupportLetterResponse => {
        return {
            dateCreated: format(createdDate, 'yyyy-MM-dd'),
            dateModified: format(createdDate, 'yyyy-MM-dd'),
            id: faker.string.uuid(),
            description: faker.lorem.sentence(),
            to: this.generateLetterAddress(createdDate),
            from: this.generateLetterAddress(createdDate),
            color: faker.datatype.boolean(),
            doubleSided: faker.datatype.boolean(),
            addressPlacement: faker.lorem.words(),
            extraService: faker.lorem.words(),
            returnEnvelope: faker.datatype.boolean(),
            perforatedPage: _.random(1, 9),
            url: faker.internet.url(),
            expectedDeliveryDate: this.recentDateAfter(createdDate),
            mailType: faker.lorem.word(),
            sendDate: this.recentDateAfter(createdDate),
            carrier: faker.company.name(),
            trackingNumber: _.uniqueId('trackingNumber'),
            trackingEvents: _.times(_.random(1, 4), () => this.generateTrackingEvent(createdDate)),
        };
    };

    private generateLetterAddress = (createdDate: Date): SupportLetterAddress => {
        return {
            dateCreated: format(createdDate, 'yyyy-MM-dd'),
            dateModified: format(createdDate, 'yyyy-MM-dd'),
            id: faker.string.uuid(),
            description: faker.lorem.sentence(),
            company: faker.company.name(),
            addressLineOne: faker.location.streetAddress(),
            city: faker.location.city(),
            state: oneOf(StateType),
            zip: faker.location.zipCode(),
            country: faker.location.country(),
        };
    };

    private generateTrackingEvent = (createdDate: Date): SupportLetterTrackingEvent => {
        return {
            dateCreated: format(createdDate, 'yyyy-MM-dd'),
            dateModified: format(createdDate, 'yyyy-MM-dd'),
            time: this.recentDateAfter(createdDate),
            name: faker.lorem.words(),
            location: `${faker.location.city()}, ${faker.location.state()}`,
        };
    };

    private generateMail = (document: ProviderDocument): ProviderMail => {
        const createdDate = document.created ? new Date(document.created) : faker.date.past();
        const mail: ProviderMail = {
            id: faker.string.uuid(),
            externalId: _.random(1e6, 1e7),
            barcode: String(_.random(1e6, 1e7)),
            itemType: _.uniqueId('itemType'),
            checks: _.random(0, 4),
            pages: _.random(1, 29),
            weight: _.random(1, 9),
            type: _.uniqueId('type'),
            receivedDate: format(createdDate, 'yyyy-MM-dd'),
            scanDate: this.recentDateAfter(createdDate),
            documentId: document.id,
            document,
            created: format(createdDate, 'yyyy-MM-dd'),
        };
        const mailCart = this.generateMailCart(mail);
        mail.mailCartId = mailCart.id;
        return mail;
    };

    private generateMailCart = (mail: ProviderMail): ProviderMailCart => {
        const createdDate = mail.created ? new Date(mail.created) : faker.date.past();
        const mailCart = {
            id: faker.string.uuid(),
            name: _.uniqueId('mailCart'),
            items: [mail],
            shipment: this.generateMailCartShipment(),
            availableMethods: upToNTimes(() => this.generateMailCartShipmentMethod(), 2),
            created: format(createdDate, 'yyyy-MM-dd'),
        };
        this.mailCarts.push(mailCart);
        return mailCart;
    };

    private generateMailCartShipment = (): ProviderMailCartShipment => {
        return {
            from: faker.lorem.words(),
            to: generateAddress(),
            method: this.generateMailCartShipmentMethod(),
            instructions: faker.lorem.sentence(),
            tracking: this.generateMailCartShipmentTracking(),
            weight: _.random(1, 25),
        };
    };

    private generateMailCartShipmentTracking = (): MailCartShipmentTracking => {
        return {
            courier: faker.lorem.words(),
            number: String(_.random(1e8, 1e9)),
            url: faker.internet.url(),
        };
    };

    private generateMailCartShipmentMethod = (): MailCartShipmentMethod => {
        return {
            externalId: _.uniqueId('externalId'),
            name: faker.lorem.words(),
            estimate: faker.lorem.sentence(),
            price: generateAmount(),
        };
    };

    private generateProvider = (): ProviderProvider => {
        const first = faker.person.firstName();
        const last = faker.person.lastName();
        const provider = {
            id: faker.string.uuid(),
            email: `${first[0]}${last}@example.test`,
            first,
            last,
            npi: String(_.random(1e5, 1e8)),
            organization: faker.company.name(),
            type: oneOf(ProviderType),
        };
        this.providers.push(provider);
        return provider;
    };

    private generateReceiveFax = (document: ProviderDocument): ProviderReceiveFax => {
        const createdDate = document.created ? new Date(document.created) : faker.date.past();
        return {
            created: format(createdDate, 'yyyy-MM-dd'),
            id: faker.string.uuid(),
            document,
            externalId: _.uniqueId('externalId'),
            pages: _.random(1, 6),
            fromE164: faker.phone.number(),
            toE164: faker.phone.number(),
            received: this.recentDateAfter(createdDate),
        };
    };

    private generateSendFax = (document: ProviderDocument): ProviderSendFax => {
        const createdDate = document.created ? new Date(document.created) : faker.date.past();
        return {
            created: format(createdDate, 'yyyy-MM-dd'),
            id: faker.string.uuid(),
            document,
            externalId: _.uniqueId('externalId'),
            pages: _.random(1, 6),
            attempts: _.random(1, 3),
            fromE164: faker.phone.number(),
            toE164: faker.phone.number(),
            success: faker.datatype.boolean(),
            result: faker.lorem.words(),
            sent: this.recentDateAfter(createdDate),
        };
    };

    private generateTransaction = (date: Date): Transaction => {
        return {
            created: this.recentDateAfter(date),
            id: faker.string.uuid(),
            amount: generateAmount(),
            memo: faker.lorem.sentence(),
            status: oneOf(TransactionStatusType),
            billedByDescriptor: faker.company.name(),
        };
    };

    private generateTransmission = (
        date: string,
        payerSubscriber?: ProviderPayerSubscriber,
    ): ProviderClaimTransmission => {
        return {
            created: date,
            id: faker.string.uuid(),
            payerSubscriberId: _.get(payerSubscriber, 'id'),
            x12: _.replace(faker.lorem.paragraphs(2), /\./g, '~'), // x12 has ~ delimiters
            statuses: _.map(subsetOf(ClaimTransmissionStatusType, _.random(1, 3)), status =>
                this.generateTransmissionStatus(status, date),
            ),
        };
    };

    private generateTransmissionStatus = (
        status: ClaimTransmissionStatusType,
        date: string,
    ): ClaimTransmissionStatus => {
        return {
            created: this.recentDateAfter(date),
            id: faker.string.uuid(),
            status,
            messages: faker.lorem.sentence(),
        };
    };

    private generateGuarantor = (claimId: string, date: string): ProviderGuarantor => {
        const first = faker.person.firstName();
        const middle = faker.person.firstName();
        const last = faker.person.lastName();

        const guarantor = {
            id: faker.string.uuid(),
            organization: faker.company.name(),
            email: `${first[0]}${middle[0]}${last}@example.test`,
            first,
            middle,
            last,
            phone: faker.phone.number(),
            addresses: [generateAddress()],
            dob: format(faker.date.past(), 'yyyy-MM-dd'),
            active: faker.datatype.boolean(),
            created: format(faker.date.past(), 'yyyy-MM-dd'),
        };
        this.guarantors.push(guarantor);
        _.times(_.random(0, 3), () => {
            const cc = this.generateCreditCard();
            this.storeToIndex('creditCards', guarantor.id, cc);
        });
        _.times(_.random(0, 7), () => {
            const checkoutCharge = this.generateCheckoutCharge();
            this.storeToIndex('checkoutCharges', guarantor.id, checkoutCharge);
        });
        const paymentPlan = this.generatePaymentPlan();
        _.set(paymentPlan, 'guarantorId', guarantor.id);
        this.storeToIndex('paymentPlans', guarantor.id, paymentPlan);

        // NB: two URLs associated with guarantor: checkout, view-bill
        const checkoutUrl = this.generateSupportShortUrl();
        this.storeToIndex('shortUrls', guarantor.id, checkoutUrl);
        const viewBillUrl = this.generateTemporaryDocumentUrl();
        this.storeToIndex('temporaryDocumentUrls', guarantor.id, viewBillUrl);

        // NB: generate some documents for this guarantor
        _.times(_.random(1, 16), () => {
            const document = this.generateDocument(claimId, this.recentDateAfter(date, 3));
            document.documentType = ProviderDisplayDocumentType.GUARANTORBILL;
            this.storeToIndex('documents', guarantor.id, document);
            const documentUrl = this.generateDocumentUrl(document.id);
            this.storeToIndex('documentUrls', guarantor.id, documentUrl);
        });
        return guarantor;
    };

    private generateCheckoutCharge = (): SupportCheckoutCharge => {
        const id = faker.string.uuid();
        return {
            id,
            amount: generateAmount(),
            feeAmount: generateAmount(),
            status: oneOf(CheckoutChargeStatusType),
            chargeDate: format(faker.date.past(), 'yyyy-MM-dd'),
            refunds: _.times(_.random(0, 2), i => this.generateCheckoutChargeRefund(id)),
        };
    };
    private generateCheckoutChargeRefund = (checkoutChargeId: string): SupportCheckoutChargeRefund => {
        return {
            id: faker.string.uuid(),
            checkoutChargeId,
            memo: faker.lorem.sentence(),
            refundedAmount: generateAmount(),
            refundedFeeAmount: generateAmount(),
            refundDate: format(faker.date.past(), 'yyyy-MM-dd'),
        };
    };
    private generateCreditCard = (): SupportCreditCard => {
        const expiry = faker.date.future({ years: 1 });
        return {
            id: faker.string.uuid(),
            last4Digits: String(_.random(1000, 9999)),
            brand: oneOf(CreditCardBrandType),
            expirationMonth: String(expiry.getMonth()),
            expirationYear: String(expiry.getFullYear()),
        };
    };
    private generatePaymentPlan = (): ProviderPaymentPlan => {
        return {
            id: faker.string.uuid(),
            intervalSpan: oneOf(PaymentPlanIntervalType),
            intervalCount: _.random(1, 3),
            invoiceId: undefined,
            guarantorId: undefined,
            amountPerCycle: generateAmount(_.random(25, 200)),
            url: faker.internet.url(),
        };
    };

    private recentDate = (): string => {
        const endDate = new Date();
        const startDate = d3.utcDay.offset(endDate, -1 * _.random(1, 7));
        return format(faker.date.between({ from: startDate, to: endDate }), 'yyyy-MM-dd');
    };

    recentDateAfter = (startDate: Date | string, offset: number = 0, range: number = 0): string => {
        if (_.isString(startDate)) {
            startDate = new Date(startDate);
        }
        const endDate = d3.utcHour.offset(startDate, offset + _.random(0, range));
        return format(endDate, 'yyyy-MM-dd');
    };

    // private dateBefore = (date: Date | string, range: number = 7): string => {
    //     if (_.isString(date)) {
    //         date = parseDate('Date')(date) as Date;
    //     }
    //     const endDate = d3.utcDay.offset(date, -1 * _.random(1, range));
    //     return format(faker.date.between(endDate, date));
    // };

    private readonly resolveProfessionalClaim = (
        pc?: ProviderProfessionalClaim,
    ): {
        facility?: ProviderFacility;
        guarantor?: ProviderGuarantor;
        payerSubscribers: ProviderPayerSubscriber[];
        subscriberPatients: ProviderSubscriberPatient[];
        payers: ProviderPayer[];
        seller?: ProviderSeller;
        subscribers: ProviderSubscriber[];
        patients: ProviderPatient[];
        providers: ProviderProvider[];
    } => {
        if (pc) {
            const { facilityId, guarantorId, payerSubscriberIds, subscriberPatientIds, providerIds, sellerId } = pc;
            const facility = _.find(this.facilities, { id: facilityId });
            const guarantor = _.find(this.guarantors, { id: guarantorId });
            const payerSubscribers = _.filter(this.payerSubscribers, hasIdIn(payerSubscriberIds));
            const subscriberPatients = _.filter(this.subscriberPatients, hasIdIn(subscriberPatientIds));
            const providers = _.filter(this.providers, hasIdIn(providerIds));
            const seller = _.find(this.sellers, { id: sellerId });
            const payers = _.flatMap(payerSubscribers, 'payer');
            const subscribers = _.flatMap(subscriberPatients, 'subscriber');
            const patients = _.flatMap(subscriberPatients, 'patient');
            return {
                facility,
                guarantor,
                patients,
                payerSubscribers,
                payers,
                seller,
                subscriberPatients,
                subscribers,
                providers,
            };
        } else {
            return {
                payerSubscribers: [],
                subscriberPatients: [],
                payers: [],
                seller: {},
                subscribers: [],
                patients: [],
                providers: [],
            };
        }
    };

    private matchesClaim = (term: string): ((c: ProviderClaim) => boolean) => {
        return (c: ProviderClaim): boolean => {
            const matchedUuid = isUuid(term) && term === c.id;
            const matchedDate = isDate(term) && (term === c.fromDos || term === c.throughDos);
            const matchedPatient = like(c.patientFirst, term) || like(c.patientLast, term);
            const matchedPayer = like(c.primaryPayerName, term);
            return matchedUuid || matchedDate || matchedPatient || matchedPayer;
        };
    };

    private storeToIndex<T>(model: string, key: string, values: T | T[]): void {
        if (!this.index.has(model)) {
            this.index.set(model, new Map<string, string[]>());
        }
        const array = _.get(this, model);
        const index = this.index.get(model);
        if (array && index) {
            const items = _.concat([], values);
            const oldIds = index.get(key) || [];
            const newIds = _.map(items, 'id');
            index.set(key, [...oldIds, ...newIds]);
            items.forEach(item => array.indexOf(item) === -1 && array.push(item));
        }
    }

    private loadFromIndex<T>(model: string, key: string): T[] | undefined {
        const array = _.get(this, model);
        const index = this.index.get(model);
        if (array && index) {
            const ids = index.get(key);
            return _.filter(array, (item: T): boolean => _.includes(ids, _.get(item, 'id')));
        }
    }
}
